import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import { API_URL } from '../config';


const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { user, login } = useContext(AuthContext);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) {
      navigate('/account/dashboard');
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          login(data.user, data.token);
          navigate('/account/dashboard');
        }, 1500);
      } else {
        setError(data.error || 'Login failed. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      setError('Network error. Please make sure the server is running.');
      setLoading(false);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_URL}/api/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: tokenResponse.access_token }),
        });
        const data = await response.json();
        
        if (data.success) {
          setSuccess(true);
          setTimeout(() => {
            login(data.user, data.token);
            navigate('/account/dashboard');
          }, 1500);
        } else {
          setError(data.error || 'Google login failed');
          setLoading(false);
        }
      } catch (err) {
        setError('Network error during Google login');
        setLoading(false);
      }
    },
    onError: () => setError('Google Login was unsuccessful'),
  });

  return (
    <main className="min-h-screen relative flex items-center justify-center py-24 px-4">
      {success && (
        <div className="fixed inset-0 z-[99999] flex flex-col items-center justify-center pointer-events-none">
          <div className="bg-[#131418] border border-white/10 shadow-2xl p-8 rounded-3xl flex flex-col items-center justify-center min-w-[250px]">
            <div className="flex gap-4 mb-6 mt-2 h-12 items-center drop-shadow-2xl">
              <div className="candle"></div>
              <div className="candle"></div>
              <div className="candle"></div>
            </div>
            <h2 className="text-[14px] font-bold text-white tracking-widest uppercase animate-pulse drop-shadow-2xl">Authenticating...</h2>
          </div>
        </div>
      )}

      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-[100%] blur-[120px] -z-10 pointer-events-none"></div>

      <div className="w-full max-w-md">
        
        {/* Form Container */}
        <div className="bg-[#131418] border border-white/5 rounded-3xl p-8 sm:p-12 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 to-transparent"></div>

          <div className="text-center mb-10">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Algo Forex <span className="text-primary">EA</span><br/>
              Access Your Trading Vault
            </h1>
            <p className="text-on-surface-variant text-[14px]">
              Log in to manage your purchased Expert Advisors and licenses.
            </p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl mb-6 text-[14px] text-center">
              {error}
            </div>
          )}

          <button 
            onClick={() => handleGoogleLogin()}
            disabled={loading || success}
            type="button" 
            className="w-full bg-[#131418] border border-white/10 hover:border-white/30 transition-colors text-white font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-3 text-[14px] mb-6 disabled:opacity-50"
          >
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            Sign in with Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="h-px bg-white/10 flex-1"></div>
            <span className="text-on-surface-variant text-[10px] font-bold tracking-widest uppercase">Or continue with email</span>
            <div className="h-px bg-white/10 flex-1"></div>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-white text-[13px] font-bold mb-2">Email Address</label>
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-[#0B0E11] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-on-surface-variant focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all text-[15px]"
                placeholder="trader@example.com"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-white text-[13px] font-bold">Password</label>
                <Link to="#" className="text-primary text-[12px] hover:underline">Forgot Password?</Link>
              </div>
              <input 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-[#0B0E11] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-on-surface-variant focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all text-[15px]"
                placeholder="••••••••"
              />
            </div>

            <button 
              type="submit" 
              disabled={loading || success}
              className={`w-full rounded-xl px-8 py-3.5 font-bold text-[15px] shadow-lg transition-all duration-300 disabled:opacity-50 ${
                success ? 'bg-green-500 text-white shadow-green-500/20 scale-[1.02]' : 'bg-primary text-[#131418] shadow-primary/20 hover:opacity-90'
              }`}
            >
              {success ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="material-symbols-outlined text-[20px]">check_circle</span>
                  Success! Redirecting...
                </span>
              ) : loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="material-symbols-outlined text-[20px] animate-spin">autorenew</span>
                  Authenticating...
                </span>
              ) : 'Log In'}
            </button>
          </form>

          <p className="text-center text-on-surface-variant text-[14px] mt-8">
            Don't have an account? <Link to="/signup" className="text-primary font-bold hover:underline">Sign up for free</Link>
          </p>
        </div>
      </div>
    </main>
  );
};

export default Login;
